import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { dialogActions } from '@/redux';

interface DialogContent {
  dialogTitle: string;
  dialogContentText?: string;
  agreeButtonText: string;
  disagreeButtonText?: string;
}

const useDialogAction = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const openDialog = useCallback(
    (content: DialogContent, handleClose: (result: boolean) => Promise<void>) => {
      dispatch(
        dialogActions.openDialog({
          content,
          handleClose,
        }),
      );
    },
    [dispatch],
  );

  const openErrorModal = useCallback(
    (errorMessage: string) => {
      openDialog(
        {
          dialogTitle: t('common.warning'),
          dialogContentText: t(errorMessage),
          agreeButtonText: t('common.form.ok'),
        },
        () => null,
      );
    },
    [openDialog, t],
  );

  const openUnsavedCloseModal = useCallback(
    (handleClose: (result: boolean) => Promise<void>) => {
      openDialog(
        {
          dialogTitle: t('common.warning'),
          dialogContentText: t('common.dialog_content_text_unsaved_quit'),
          agreeButtonText: t('common.form.ok'),
          disagreeButtonText: t('common.form.cancel'),
        },
        handleClose,
      );
    },
    [openDialog, t],
  );

  const openTableDeleteRowModal = useCallback(
    (name: string, handleClose: (result: boolean) => Promise<void>) => {
      openDialog(
        {
          dialogTitle: t('common.warning'),
          dialogContentText: t('common.dialog_content_text_delete_with_param', {
            name,
          }),
          agreeButtonText: t('common.form.delete'),
          disagreeButtonText: t('common.form.cancel'),
        },
        handleClose,
      );
    },
    [openDialog, t],
  );

  return {
    openDialog,
    openErrorModal,
    openUnsavedCloseModal,
    openTableDeleteRowModal,
  };
};

export default useDialogAction;
